// questionKeys — where a key takes the caret in a question, pure.
//
// A question is typed with plain keys (pen "Always writing", piece 6): its
// lines run stem, answer A, A's why line, answer B, B's why line, … Enter
// goes down that list, starting a new answer after the last why line;
// Enter on an empty answer ends the question, and the caret goes on in an
// ordinary line below it. Backspace on an empty line goes back up (an
// empty answer goes with it). No chords.
//
// This module only decides: given the question as shown, the line the
// caret is in and the key, it names the next caret place and the one edit
// that goes with it. The renderer moves its caret (view state) and hands
// the edit to the host, which saves it like any other.

export type { QuestionKey, QuestionLine, QuestionStep };
export { questionStep };

import { exhaustSwitchCase } from "@bilda/utils";
import type { McqElement } from "./types.js";

// A line of the question, by the answer's place in the list.
type QuestionLine =
    | { readonly type: "stem" }
    | { readonly type: "answer"; readonly index: number }
    | { readonly type: "why"; readonly index: number };

// A key the question acts on. Enter carries the line's text as it was left.
type QuestionKey =
    | { readonly type: "enter"; readonly text: string }
    | { readonly type: "backspace-empty" };

type QuestionStep =
    // The caret goes to another line of the question.
    | { readonly type: "caret"; readonly to: QuestionLine }
    // A new empty answer at the end, the caret in it.
    | { readonly type: "add-answer"; readonly to: QuestionLine }
    // The answer goes (it was empty), the caret to the line above.
    | {
          readonly type: "remove-answer";
          readonly choiceId: string;
          readonly to: QuestionLine;
      }
    // The question ends: an ordinary line below it takes the caret; the
    // empty answer Enter was pressed in goes too (absent: it stays, being
    // the only or the right one).
    | { readonly type: "end-question"; readonly removes: string | undefined }
    // At the answers ceiling: no new answer; the Limit hint says why.
    | { readonly type: "full" }
    // Nothing to do here.
    | { readonly type: "stay" };

const answer = (index: number): QuestionLine => ({ type: "answer", index });
const why = (index: number): QuestionLine => ({ type: "why", index });
const stem: QuestionLine = { type: "stem" };

// The line above answer `index` (the previous answer's why line, or the
// stem above the first).
const above = (index: number): QuestionLine =>
    index > 0 ? why(index - 1) : stem;

// The next answer after `index`, or a new one at the end (the ceiling
// permitting).
const nextAnswer = (
    question: McqElement,
    max: number,
    index: number,
): QuestionStep => {
    const count = question.choices.length;
    if (index + 1 < count) return { type: "caret", to: answer(index + 1) };
    return count >= max
        ? { type: "full" }
        : { type: "add-answer", to: answer(count) };
};

// An answer that may go: not the only one, and not the right one (a
// question always keeps exactly one right answer).
const removable = (question: McqElement, index: number): string | undefined => {
    const choice = question.choices[index];
    return choice === undefined ||
        choice.correct ||
        question.choices.length <= 1
        ? undefined
        : choice.id;
};

// O(1) in the question's answers (a lookup by place).
const questionStep = (
    question: McqElement,
    max: number,
    at: QuestionLine,
    key: QuestionKey,
): QuestionStep => {
    switch (key.type) {
        case "enter":
            switch (at.type) {
                case "stem":
                    return nextAnswer(question, max, -1);
                case "answer":
                    return key.text.trim() === ""
                        ? {
                              type: "end-question",
                              removes: removable(question, at.index),
                          }
                        : { type: "caret", to: why(at.index) };
                case "why":
                    return nextAnswer(question, max, at.index);
                default:
                    return exhaustSwitchCase(at);
            }
        case "backspace-empty":
            switch (at.type) {
                case "stem":
                    return { type: "stay" };
                case "answer": {
                    const choiceId = removable(question, at.index);
                    return choiceId === undefined
                        ? { type: "caret", to: above(at.index) }
                        : {
                              type: "remove-answer",
                              choiceId,
                              to: above(at.index),
                          };
                }
                case "why":
                    return { type: "caret", to: answer(at.index) };
                default:
                    return exhaustSwitchCase(at);
            }
        default:
            return exhaustSwitchCase(key);
    }
};
